import React, { memo, type TeactNode, useEffect, useRef, useState } from '../../lib/teact/teact';
import { setExtraStyles } from '../../lib/teact/teact-dom';

import { requestForcedReflow, requestMeasure } from '../../lib/fasterdom/fasterdom';
import buildClassName from '../../util/buildClassName';

import styles from './OverlayScrollbar.module.scss';

const MIN_THUMB_PX = 24;

export function getOverlayScrollbarGeometry(
  height: number, scrollHeight: number, scrollTop: number, trackHeight: number,
) {
  const range = Math.max(0, scrollHeight - height);
  const size = scrollHeight > 0
    ? Math.min(trackHeight, Math.max(MIN_THUMB_PX, trackHeight * height / scrollHeight)) : 0;
  const travel = Math.max(0, trackHeight - size);
  const offset = range > 0 ? Math.max(0, Math.min(range, scrollTop)) / range * travel : 0;
  return { range, size, travel, offset };
}

type Props = {
  children: TeactNode;
  isActive: boolean;
  className?: string;
  viewportClassName?: string;
  contentClassName?: string;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
};

function OverlayScrollbar({
  children, isActive, className, viewportClassName, contentClassName, onScroll,
}: Props) {
  const viewportRef = useRef<HTMLDivElement>();
  const contentRef = useRef<HTMLDivElement>();
  const trackRef = useRef<HTMLDivElement>();
  const thumbRef = useRef<HTMLDivElement>();
  const [isScrollable, setIsScrollable] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setIsDragging(false);
    const viewport = viewportRef.current;
    const content = contentRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!isActive || !viewport || !content || !track || !thumb) {
      setIsScrollable(false);
      return undefined;
    }
    let alive = true;
    let queued = false;
    let pointerId: number | undefined;
    let drag: { top: number; grab: number; range: number; travel: number } | undefined;
    const geometry = () => getOverlayScrollbarGeometry(
      viewport.clientHeight, viewport.scrollHeight, viewport.scrollTop, track.clientHeight,
    );
    const refresh = () => {
      if (queued || !alive) return;
      queued = true;
      requestForcedReflow(() => {
        queued = false;
        if (!alive) return undefined;
        const { range, size, offset, travel } = geometry();
        return () => {
          if (!alive) return;
          setIsScrollable(range > 0 && travel > 0);
          setExtraStyles(thumb, { height: `${size}px`, transform: `translateY(${offset}px)` });
        };
      });
    };
    const stopDrag = () => {
      if (pointerId !== undefined && track.hasPointerCapture?.(pointerId)) track.releasePointerCapture(pointerId);
      pointerId = undefined;
      drag = undefined;
      if (alive) setIsDragging(false);
    };
    const move = (y: number) => {
      if (!drag || !alive || drag.travel <= 0) return;
      const top = Math.max(0, Math.min(drag.travel, y - drag.top - drag.grab)) / drag.travel * drag.range;
      requestMeasure(() => {
        if (!alive) return;
        viewport.scrollTo({ top, behavior: 'auto' });
        refresh();
      });
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || event.isPrimary === false) return;
      event.preventDefault();
      event.stopPropagation();
      pointerId = event.pointerId;
      track.setPointerCapture?.(pointerId);
      requestForcedReflow(() => {
        if (!alive || pointerId !== event.pointerId) return undefined;
        const { range, travel, size, offset } = geometry();
        const top = track.getBoundingClientRect().top;
        const onThumb = thumb.contains(event.target as Node);
        drag = { top, range, travel, grab: onThumb ? event.clientY - top - offset : size / 2 };
        return () => {
          if (!alive || pointerId !== event.pointerId) return;
          setIsDragging(true);
          if (!onThumb) move(event.clientY);
        };
      });
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return;
      event.preventDefault();
      move(event.clientY);
    };
    const handlePointerEnd = (event: PointerEvent) => {
      if (pointerId === event.pointerId) stopDrag();
    };
    const handleClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      requestMeasure(() => {
        if (!alive) return;
        const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientHeight : 1);
        viewport.scrollBy({ top: delta, behavior: 'auto' });
        refresh();
      });
    };
    const handleResize = () => {
      stopDrag();
      refresh();
    };
    viewport.addEventListener('scroll', refresh, { passive: true });
    track.addEventListener('pointerdown', handlePointerDown);
    track.addEventListener('pointermove', handlePointerMove);
    track.addEventListener('pointerup', handlePointerEnd);
    track.addEventListener('pointercancel', handlePointerEnd);
    track.addEventListener('lostpointercapture', stopDrag);
    track.addEventListener('click', handleClick);
    track.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('resize', handleResize);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handleResize) : undefined;
    observer?.observe(viewport);
    observer?.observe(content);
    refresh();
    return () => {
      alive = false;
      stopDrag();
      observer?.disconnect();
      viewport.removeEventListener('scroll', refresh);
      track.removeEventListener('pointerdown', handlePointerDown);
      track.removeEventListener('pointermove', handlePointerMove);
      track.removeEventListener('pointerup', handlePointerEnd);
      track.removeEventListener('pointercancel', handlePointerEnd);
      track.removeEventListener('lostpointercapture', stopDrag);
      track.removeEventListener('click', handleClick);
      track.removeEventListener('wheel', handleWheel);
      window.removeEventListener('resize', handleResize);
    };
  }, [isActive, children]);

  return (
    <div
      className={buildClassName(
        styles.wrapper, className, isScrollable && styles.scrollable, isDragging && styles.dragging,
      )}
    >
      <div
        ref={viewportRef}
        className={buildClassName(styles.viewport, 'custom-scroll', viewportClassName)}
        onScroll={onScroll}
      >
        <div ref={contentRef} className={contentClassName}>{children}</div>
      </div>
      <div ref={trackRef} className={styles.track} aria-hidden="true">
        <div ref={thumbRef} className={styles.thumb} />
      </div>
    </div>
  );
}

export default memo(OverlayScrollbar);
